import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';

import { KioskButton } from '../components/KioskButton';
import { KioskFrame } from '../components/KioskFrame';
import { listSpots, type SpotSummary } from '../lib/api';
import { pickI18n } from '../lib/format';
import { type Locale } from '../lib/i18n';
import { useKiosk } from '../lib/session';

/**
 * 景點描述為 CMS 所見即所得編輯器輸出之 HTML；公開端渲染前以 DOMPurify 白名單淨化
 * （只留格式標籤 + a[href]），防直接打 API 寫入之惡意 HTML（XSS）。舊資料若為純文字
 * 也相容（無標籤即原樣顯示）。連結強制 rel=noopener 避免 tabnabbing。
 */
const SPOT_DESC_ALLOWED = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'ul', 'ol', 'li', 'a', 'span'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
};
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('rel', 'noopener noreferrer');
    node.setAttribute('target', '_blank');
  }
});
function sanitizeSpotDescription(html: string): string {
  return DOMPurify.sanitize(html, SPOT_DESC_ALLOWED);
}

/**
 * F-3 景點介紹 — Flow 3.x「認識安平」入口。
 *
 * GET /spots 為公開端點；前端過濾 `is_published === true` 取已發布。
 * 顯示三語 name + description（依當前 locale），含座標 + 觸發半徑供未來
 * 窗屏 / 手機 App 位置感知導覽（PRD §C-1）展望。
 */
export function SpotsScreen(): JSX.Element {
  const navigate = useNavigate();
  const { t, locale } = useKiosk();

  const spotsQuery = useQuery({ queryKey: ['spots'], queryFn: listSpots });
  const items = (spotsQuery.data ?? []).filter((s) => s.is_published);

  return (
    <KioskFrame>
      <h1 className="k-h1">{t('spots.title')}</h1>

      {spotsQuery.isPending && <p className="k-text">{t('common.loading')}</p>}
      {spotsQuery.isError && <p className="k-error">{t('common.errNetwork')}</p>}
      {spotsQuery.isSuccess && items.length === 0 && (
        <p className="k-text k-text--dim">{t('spots.empty')}</p>
      )}

      {items.map((s) => (
        <SpotCard key={s.id} spot={s} locale={locale} t={t} />
      ))}

      <div className="k-footbar">
        <KioskButton variant="secondary" onClick={() => navigate('/home')}>
          ← {t('common.home')}
        </KioskButton>
      </div>
    </KioskFrame>
  );
}

function SpotCard({
  spot,
  locale,
  t,
}: {
  spot: SpotSummary;
  locale: Locale;
  t: (key: string) => string;
}): JSX.Element {
  const name = pickI18n(spot.name_i18n, spot.spot_code, locale);
  const description = spot.description_i18n ? pickI18n(spot.description_i18n, '', locale) : '';
  return (
    <div className="k-card" style={{ marginBottom: 12 }}>
      <h2 className="k-h2" style={{ marginBottom: 8 }}>
        {name}
      </h2>
      {description && (
        <div
          className="k-text"
          style={{ marginBottom: 12, lineHeight: 1.6 }}
          dangerouslySetInnerHTML={{ __html: sanitizeSpotDescription(description) }}
        />
      )}
      <p className="k-text k-text--dim" style={{ fontSize: 13 }}>
        {t('spots.coords')}：{Number(spot.latitude).toFixed(4)}, {Number(spot.longitude).toFixed(4)}
        {'\u3000\u00b7\u3000'}
        {t('spots.radius')}：{spot.trigger_radius_m} m
      </p>
    </div>
  );
}
